import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';

/*
 * Constants.
 */

const MergeInformationBullets = [
  {
    id: 1,
    node: (
      <Trans id="contact-merge-info-banner-info-bullet-multiple-values">
        Fields that can have multiple values (like handles) are all preserved.
      </Trans>
    ),
  },
  {
    id: 2,
    node: (
      <Trans id="contact-merge-info-banner-info-bullet-pick-value-to-keep">
        If there are conflicting values for a unique value field (like the name), you’ll get to pick the one
        to keep.
      </Trans>
    ),
  },
  {
    id: 3,
    node: (
      <Trans id="contact-merge-info-banner-info-bullet-merge-cannot-be-undone">
        Merging cannot be undone.
      </Trans>
    ),
  },
];
const MergeHelpLink = 'https://help.front.com/t/y7bw8f/how-to-merge-contacts';

/*
 * Props.
 */

interface ContactMergeInformationBannerProps {
  isInfoBoxExpanded: boolean;
  onToggle: () => void;
}

/*
 * Styles.
 */

const StyledInformationBannerContainerDiv = styled.div`
  position: relative;
  background: ${(p) => p.theme.palette.blue.shade20};
  border-radius: 16px;
  margin: 12px -8px;
`;

const StyledInformationHeaderDiv = styled.div`
  display: flex;
  flex-flow: row;
  padding: 10px 12px 0 12px;
`;

const StyledInformationHeaderTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.small};
  margin-left: 8px;
`;

const StyledInfoBulletsUl = styled.ul`
  padding-left: 33px;
  margin: 8px 0 0 0;
`;

const StyledInfoBulletLi = styled.li`
  font-size: ${fontSizes.verySmall};
  padding-right: 32px;
  padding-left: 5px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledCloseIconContainerDiv = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
`;

const StyledLinkButton = styled(Button)`
  padding: 8px 0 8px 27px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledButtonContainerDiv = styled.div`
  display: flex;
`;

const StyledButtonTextSpan = styled.span`
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.bold};
  margin-right: 6.5px;
`;

/*
 * Component.
 */

export const ContactMergeInformationBanner: FC<ContactMergeInformationBannerProps> = (props) => {
  const theme = useUserTheme();
  const {isInfoBoxExpanded, onToggle} = props;

  if (!isInfoBoxExpanded) {
    return null;
  }
  return (
    <StyledInformationBannerContainerDiv>
      <StyledCloseIconContainerDiv>
        <IconButton
          iconName="closeSmall"
          color={theme.greys.shade70}
          label={<Trans id="contact-merge-info-banner-close">Close</Trans>}
          onClick={onToggle}
        />
      </StyledCloseIconContainerDiv>
      <StyledInformationHeaderDiv>
        <Icon name="infoFilledSmall" color={theme.palette.blue.shade40} />
        <StyledInformationHeaderTitleDiv>
          <Trans id="contact-merge-info-banner-title">How does merging work?</Trans>
        </StyledInformationHeaderTitleDiv>
      </StyledInformationHeaderDiv>
      <StyledInfoBulletsUl>
        {MergeInformationBullets.map((bullet) => (
          <StyledInfoBulletLi key={bullet.id}>{bullet.node}</StyledInfoBulletLi>
        ))}
      </StyledInfoBulletsUl>
      <StyledButtonContainerDiv>
        <StyledLinkButton
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={() => openInBrowser(MergeHelpLink)}
          isRounded
        >
          <StyledButtonContentDiv>
            <StyledButtonTextSpan>
              <Trans id="contact-merge-info-banner-learn-more">LEARN MORE</Trans>
            </StyledButtonTextSpan>
            <Icon name="externalLinkSmall" />
          </StyledButtonContentDiv>
        </StyledLinkButton>
      </StyledButtonContainerDiv>
    </StyledInformationBannerContainerDiv>
  );
};
